import React from 'react'
import PropTypes from 'prop-types'

import { Table, TableBody, TableHeader,
  TableHeaderColumn, TableRow, TableRowColumn } from 'material-ui/Table'

import TimeFormatter from 'infra/TimeFormatter'

export default class LineItemsDetailsContentTable extends React.Component {

  static propTypes = {
    loading: PropTypes.bool.isRequired,
    mode: PropTypes.string.isRequired,
    lineItems: PropTypes.array.isRequired,
    workers: PropTypes.array.isRequired,
    allocations: PropTypes.shape().isRequired,
    onAllocationTimeChanged: PropTypes.func.isRequired
  }

  constructor(props) {
    super(props)
    this.state = {}
  }

  handleInputChanged = (event, lineItem, worker) => {
    event.preventDefault()
    // Validate input
    const value = event.target.value
    const validationResult = this.validateInput(value)
    if (validationResult.success) {
      this.props.onAllocationTimeChanged(lineItem, worker, validationResult.timeInMinutes)
      this.setState({
        editingLineItemId: null,
        editingWorkerId: null
      })
    }
  }

  validateInput = (value) => {
    const result = parseFloat(value)
    if (isNaN(result)) {
      return {
        error: 'Invalid input value. Please, enter a number.'
      }
    }
    return {
      success: true,
      timeInMinutes: Math.floor(result * 60)
    }
  }

  getAllocationTime = (lineItem, worker) => {
    const allocations = this.props.allocations
    if (allocations.hasOwnProperty(lineItem.id)) {
      const itemAllocations = allocations[lineItem.id]
      if (itemAllocations.hasOwnProperty(worker.id)) {
        const timeInMinutes = itemAllocations[worker.id].timeInMinutes
        this.rowAccumulator[worker.id] =
          (this.rowAccumulator[worker.id] || 0) + timeInMinutes
        this.columnAccumulator[lineItem.id] = 
          (this.columnAccumulator[lineItem.id] || 0) + timeInMinutes
        this.total = this.total + timeInMinutes
        return timeInMinutes
      }
    }
    return 0
  }

  getAllocationDisplay = (lineItem, worker) => {
    return TimeFormatter.toHoursMinutesFormat(this.getAllocationTime(lineItem, worker))
  }

  getAllocationEdit = (lineItem, worker) => {
    return (this.getAllocationTime(lineItem, worker) / 60).toFixed(1)
  }

  getRowTotal = (worker) => {
    return TimeFormatter.toHoursMinutesFormat(this.rowAccumulator[worker.id] || 0)
  }

  getColumnTotal = (lineItem) => {
    return TimeFormatter.toHoursMinutesFormat(this.columnAccumulator[lineItem.id] || 0)
  }

  getTotal = () => {
    return TimeFormatter.toHoursMinutesFormat(this.total)
  }

  handleEditingRequest = (event, lineItem, worker) => {
    event.preventDefault()
    if (lineItem.id !== this.state.editingLineItemId
      || worker.id !== this.state.editingWorkerId) {
      this.setState({
        editingLineItemId: lineItem.id,
        editingWorkerId: worker.id
      })
    }
  }

  componentDidUpdate() {
    if (this.editingInput) {
      this.editingInput.focus()      
    }
  }
  
  render() {
    const lineItems = this.props.lineItems
    const workers = this.props.workers
    this.rowAccumulator = {}
    this.columnAccumulator = {}
    this.total = 0
    return (
      this.props.loading
        // TODO: Improve loading indicator layout
        ? <div>Loading ...</div>  
        : <Table fixedHeader={false} fixedFooter={false} 
          bodyStyle={styles.tableContainer}
          wrapperStyle={styles.tableBody}>
          <TableHeader
            adjustForCheckbox={false}
            displaySelectAll={false}>
            <TableRow style={styles.row}>
              <TableHeaderColumn key={'start'} style={{ ...styles.driverCell, ...styles.itemCell }}></TableHeaderColumn>
              { lineItems.map((item, index) => 
                <TableHeaderColumn key={index} style={styles.driverCell}>
                  { item.name }
                </TableHeaderColumn>)
              }
              <TableHeaderColumn key='total' style={styles.driverCell}>
                Total
              </TableHeaderColumn>
            </TableRow>
          </TableHeader>
          <TableBody displayRowCheckbox={false}>
            { workers.map((worker, workerIndex) => 
              <TableRow key={workerIndex} style={styles.row} selectable={false}>
                <TableRowColumn key={workerIndex} style={{ ...styles.driverCell, ...styles.itemCell }}>
                  { worker.name }
                </TableRowColumn>
                { lineItems.map((lineItem, itemIndex) => 
                  <TableRowColumn key={`${workerIndex}-${itemIndex}`}
                    style={styles.dataCell}>
                    {/* Allow edits only on daily breakdowns, totals is readonly */}
                    { this.props.mode === 'day' && this.state.editingLineItemId === lineItem.id
                      && this.state.editingWorkerId === worker.id
                      // Update value after editing complete providing defaultValue and 
                      // handling change on the onBlur (focus lost) event
                      ? <input style={styles.input}
                        ref={input => this.editingInput = input}
                        defaultValue={this.getAllocationEdit(lineItem, worker)}
                        onBlur={e => this.handleInputChanged(e, lineItem, worker)}
                        type='text' />
                      : <div style={styles.dataContainer}
                        onClick={e => this.handleEditingRequest(e, lineItem, worker)}>
                        { this.getAllocationDisplay(lineItem, worker) }
                      </div>
                    }
                  </TableRowColumn>
                )}
                <TableRowColumn key='total' style={{ ...styles.dataCell, ...styles.totalsCell }}>
                  { this.getRowTotal(worker) }
                </TableRowColumn>)
              </TableRow>
            )}
            <TableRow key='total'>
              <TableRowColumn style={styles.driverCell}>Total</TableRowColumn>
              { lineItems.map((lineItem, index) => 
                <TableHeaderColumn key={index} style={styles.driverCell}>
                  { this.getColumnTotal(lineItem) }
                </TableHeaderColumn>)
              }
              <TableRowColumn key='total' style={styles.driverCell}>
                { this.getTotal() }
              </TableRowColumn>
            </TableRow>
          </TableBody>
        </Table>
    )
  }

}

const styles = {

  row: {
    borderTop: 'solid 1px #979797',
    height: 40
  },

  driverCell: {
    backgroundColor: '#f3f3f3',
    textAlign: 'center',
    borderRight: 'solid 1px #979797',
    borderBottom: 'solid 1px #979797',
    color: '#757575',
    width: 60,
    height: 40,
    whiteSpace: 'normal',
    paddingLeft: 12,
    paddingRight: 12,
    paddingTop: 6,
    paddingBottom: 6
  },

  itemCell: {
    width: 80
  },

  input: {
    height: 50,
    border: 'none',
    textAlign: 'center',
    width: '100%',
    padding: 0,
    outlineWidth: 4
  },

  dataContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    width: '100%'
  },

  dataCell: {
    padding: 0,
    borderRight: 'solid 1px #979797',
    borderBottom: 'solid 1px #979797',
    textAlign: 'center',
    color: '#757575',
    height: 40,
    width: 60
  },

  totalsCell: {
    backgroundColor: '#f3f3f3'    
  },

  tableContainer: {
    maxHeight: 700,
    overflow: 'unset'
  },

  tableBody: {
    maxHeight: 700,
    overflow: 'auto'
  }

}



// WEBPACK FOOTER //
// ./src/schedule/common/modules/lineItems/LineItemsDetailsContentTable.js